import React, { useEffect, useMemo } from 'react';
import { DeliveryDetailDataForm } from '../../../../app/models/DeliveryDetail';
import { Batch } from '../../../../app/models/Batch';
import { SalesOrderDetail } from '../../../../app/models/SalesOrderDetail';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import SelectTwo, { SelectTwoMapperOptions } from '../../../../components/SelectTwo';

interface Props {
    detail: DeliveryDetailDataForm;
    setDetail: (detail: DeliveryDetailDataForm) => void;
    batches: Batch[];
    salesOrderDetails: SalesOrderDetail[];
    errorFields?: any;
    is_edit?: boolean;
    // Opcional: modo multi-lote (crear varios antes de guardar)
    pendingRows?: Array<{ batch_id: number; quantity: number }>;
    onAddRow?: () => void;
    onRemoveRow?: (index: number) => void;
    onChangePendingRow?: (
        index: number,
        patch: { batch_id?: number; quantity?: number }
    ) => void;
}

const DeliveryDetailForm = ({
    detail,
    setDetail,
    batches,
    salesOrderDetails,
    errorFields,
    is_edit,
    pendingRows,
    onAddRow,
    onRemoveRow,
    onChangePendingRow
}: Props) => {
    const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors } = useHandleErrorFields();

    useEffect(() => {
        setErrors(errorFields);
    }, [errorFields]);

    // Obtener la posición del pedido seleccionada
    const selectedSalesOrderDetail = useMemo(() => {
        return salesOrderDetails.find((sod) => sod.id === detail.sales_order_detail_id);
    }, [salesOrderDetails, detail.sales_order_detail_id]);

    // Obtener el lote seleccionado
    const selectedBatch = useMemo(() => {
        return batches.find((batch) => batch.id === detail.batch_id);
    }, [batches, detail.batch_id]);

    const handleChangeSelectTwo = (name: string, value: number | string | null) => {
        const updatedDetail = { ...detail, [name]: value };
        
        // Si se selecciona una posición del pedido, limpiar lote y cantidad
        if (name === 'sales_order_detail_id') {
            updatedDetail.batch_id = undefined;
            updatedDetail.quantity = 0;
        }
        
        // Si se selecciona un lote, actualizar unit_id y cantidad automáticamente
        if (name === 'batch_id') {
            const batch = batches.find((b) => b.id === value);
            if (batch) {
                updatedDetail.unit_id = batch.unit_id || undefined;
                // Heredar cantidad restante del lote (considerando lo ya preparado)
                const remaining = getRemainingQuantityForBatch(batch.id!);
                updatedDetail.quantity = remaining > 0 ? remaining : 0;
            }
        }
        
        setDetail(updatedDetail);
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        
        // Para cantidad, validar que no exceda la cantidad del lote
        if (name === 'quantity') {
            const numericValue = value === '' ? undefined : Number(value);
            
            // Validación básica: cantidad no puede exceder la cantidad del lote
            // Para entregas, validamos contra batch.quantity (cantidad disponible en el lote)
            // La validación completa (considerando entregas ya realizadas) se hace en el backend
            let finalQuantity = numericValue;
            if (selectedBatch && numericValue !== undefined && numericValue >= 0) {
                const batchQuantity = Number(selectedBatch.quantity) || 0;
                
                if (numericValue > batchQuantity) {
                    // Limitar al máximo del lote
                    finalQuantity = batchQuantity;
                }
            }
            
            const updatedDetail = { ...detail, [name]: finalQuantity };
            setDetail(updatedDetail);
        } else {
            setDetail({ ...detail, [name]: value });
        }
    };

    // Cantidad restante por lote considerando lo ya preparado en pendingRows
    const getRemainingQuantityForBatch = (batchId: number): number => {
        const batch = batches.find(b => b.id === batchId);
        if (!batch) return 0;
        const baseQty = Number(batch.quantity) || 0;

        const usedInPending =
            (pendingRows || []).reduce((acc, row) => {
                return row.batch_id === batchId ? acc + (Number(row.quantity) || 0) : acc;
            }, 0);

        const usedInCurrent =
            detail.batch_id === batchId ? Number(detail.quantity) || 0 : 0;

        return baseQty - usedInPending - usedInCurrent;
    };

    // Preparar opciones de posiciones del pedido: Posición, Producto, Cantidad, Unidad
    const salesOrderDetailOptions = useMemo(() => {
        return salesOrderDetails
            .filter((sod) => sod.id !== undefined)
            .map((sod) => {
                const position = sod.position || sod.id || 0;
                const product = sod.product || 'Sin producto';
                const quantity = sod.quantity != null ? Number(sod.quantity) : 0;
                const unit = sod.quantity_unit || '';
                const quantityStr = typeof quantity === 'number' && !Number.isNaN(quantity) ? quantity.toFixed(2) : '0';
                const label = `Posición #${position} - ${product} - ${quantityStr} ${unit}`.trim();
                return {
                    value: sod.id as number,
                    label
                };
            });
    }, [salesOrderDetails]);

    // Opciones base de lotes filtrados por producto, ordenadas
    const baseBatchOptions = useMemo(() => {
        if (!selectedSalesOrderDetail?.product_id) return [];
        return batches
            .filter(b => b.id && b.product_id === selectedSalesOrderDetail.product_id)
            .sort((a, b) => (b.id || 0) - (a.id || 0));
    }, [batches, selectedSalesOrderDetail]);

    // Opciones para el select principal (excluye lotes sin cantidad restante)
    const batchOptions = useMemo(() => {
        return baseBatchOptions
            .filter(batch => getRemainingQuantityForBatch(batch.id!) > 0)
            .map(batch => {
                const remaining = getRemainingQuantityForBatch(batch.id!);
                return {
                    value: batch.id as number,
                    label: `#${batch.id} - ${batch.product || ''} - ${remaining} ${batch.unit || ''}`
                };
            });
    }, [baseBatchOptions, pendingRows, detail.batch_id, detail.quantity]);

    // Opciones para una fila preparada específica:
    // incluye su propio lote + lotes con cantidad restante disponible
    const getBatchOptionsForPendingRow = (rowIdx: number) => {
        return baseBatchOptions
            .filter(batch => {
                if (!batch.id) return false;
                const ownBatchId = pendingRows?.[rowIdx]?.batch_id;
                if (batch.id === ownBatchId) return true; // siempre incluir el propio
                const remaining = getRemainingQuantityForBatch(batch.id!);
                return remaining > 0;
            })
            .map(batch => {
                const ownBatchId = pendingRows?.[rowIdx]?.batch_id;
                const remaining = batch.id === ownBatchId
                    ? (pendingRows?.[rowIdx]?.quantity ?? 0)
                    : getRemainingQuantityForBatch(batch.id!);
                return {
                    value: batch.id as number,
                    label: `#${batch.id} - ${batch.product || ''} - ${remaining} ${batch.unit || ''}`
                };
            });
    };

    return (
        <>
            {/* Posición del Pedido */}
            <div className="mb-3">
                <label className="form-label">Posición de Pedido *</label>
                <SelectTwo
                    name="sales_order_detail_id"
                    id="sales_order_detail_id"
                    inputValue={detail.sales_order_detail_id}
                    options={salesOrderDetailOptions}
                    hasError={fieldHasError('sales_order_detail_id') !== ''}
                    onChange={(value: any) => handleChangeSelectTwo('sales_order_detail_id', value?.value)}
                    placeholder="Seleccione una posición del pedido"
                    onFocus={() => onFocusRemove('sales_order_detail_id')}
                    isClearable={false}
                    disabled={is_edit === true}
                />
                <div className="invalid-feedback" style={{ display: 'flex' }}>
                    {fieldErrorMessage('sales_order_detail_id')}
                </div>
            </div>

            {/* Producto (heredado de la posición - solo lectura) */}
            {selectedSalesOrderDetail && (
                <div className="mb-3">
                    <label className="form-label">Producto</label>
                    <input
                        type="text"
                        className="form-control"
                        value={selectedSalesOrderDetail.product || ''}
                        disabled
                        readOnly
                        style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                    />
                    <small className="form-text text-muted">Heredado de la posición seleccionada</small>
                </div>
            )}

            {/* Lote + Cantidad + Unidad en una sola línea */}
            <div className="mb-3">
                <label className="form-label">Lote / Cantidad / Unidad *</label>
                <div className="d-flex gap-2 align-items-start">
                    {/* Lote — más ancho */}
                    <div style={{ flex: '3 1 0', minWidth: 0 }}>
                        <SelectTwo
                            name="batch_id"
                            id="batch_id"
                            inputValue={detail.batch_id}
                            options={batchOptions}
                            hasError={fieldHasError('batch_id') !== ''}
                            onChange={(value: any) => handleChangeSelectTwo('batch_id', value?.value)}
                            placeholder="Seleccione un lote"
                            onFocus={() => onFocusRemove('batch_id')}
                            isClearable={false}
                            disabled={!selectedSalesOrderDetail}
                        />
                        <div className="invalid-feedback" style={{ display: 'flex' }}>
                            {fieldErrorMessage('batch_id')}
                        </div>
                        {!selectedSalesOrderDetail && (
                            <small className="form-text text-muted">
                                Primero debe seleccionar una posición del pedido
                            </small>
                        )}
                    </div>

                    {/* Cantidad */}
                    <div style={{ flex: '1 1 0', minWidth: '80px', position: 'relative' }}>
                        <input
                            name="quantity"
                            type="number"
                            step="1"
                            min="0"
                            value={detail.quantity ?? ''}
                            onChange={handleChange}
                            className={`form-control ${fieldHasError('quantity')}`}
                            placeholder="0"
                            onFocus={() => onFocusRemove('quantity')}
                            autoComplete="off"
                            style={{ height: '38px' }}
                            disabled={!selectedSalesOrderDetail || !selectedBatch}
                        />
                        <div className="invalid-feedback" style={{ display: 'flex' }}>
                            {fieldErrorMessage('quantity')}
                        </div>
                    </div>

                    {/* Unidad */}
                    <div style={{ width: '100px', flexShrink: 0 }}>
                        <input
                            type="text"
                            className="form-control"
                            value={selectedBatch?.unit || selectedSalesOrderDetail?.quantity_unit || ''}
                            disabled
                            readOnly
                            placeholder="Unidad"
                            style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed', height: '38px' }}
                        />
                    </div>

                    {/* Espacio para alinear con botón eliminar de filas preparadas */}
                    {onAddRow && <div style={{ width: '40px', flexShrink: 0 }} />}
                </div>
                {selectedSalesOrderDetail && !selectedBatch && (
                    <small className="form-text text-muted mt-1 d-block">
                        Unidad solicitada: {selectedSalesOrderDetail.quantity_unit || 'N/A'}
                    </small>
                )}
            </div>

            {/* Botón para agregar más lotes (solo en modo multi-lote) */}
            {onAddRow && (
                <div className="mb-3 d-flex justify-content-end">
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={onAddRow}
                        disabled={!detail.batch_id || !detail.quantity || Number(detail.quantity) <= 0}
                    >
                        Agregar lote
                    </button>
                </div>
            )}

            {/* Lista de lotes agregados (solo en modo multi-lote) */}
            {pendingRows && pendingRows.length > 0 && (
                <div className="mb-3">
                    <small className="text-muted d-block mb-2">Lotes preparados para guardar:</small>
                    {pendingRows.map((row, idx) => {
                        const batch = batches.find(b => b.id === row.batch_id);
                        const rowOptions = getBatchOptionsForPendingRow(idx);
                        return (
                            <div
                                key={`${row.batch_id}-${idx}`}
                                className="d-flex align-items-start gap-2 mb-2"
                            >
                                {/* Lote — mismo flex que la fila principal */}
                                <div style={{ flex: '3 1 0', minWidth: 0 }}>
                                    <SelectTwo
                                        name={`pending_batch_${idx}`}
                                        id={`pending_batch_${idx}`}
                                        inputValue={row.batch_id}
                                        options={rowOptions}
                                        hasError={false}
                                        onChange={(value: any) => {
                                            if (!onChangePendingRow || !value) return;
                                            onChangePendingRow(idx, { batch_id: value.value as number });
                                        }}
                                        placeholder="Seleccione un lote"
                                        isClearable={false}
                                    />
                                </div>

                                {/* Cantidad */}
                                <div style={{ flex: '1 1 0', minWidth: '80px' }}>
                                    <input
                                        type="number"
                                        step="1"
                                        min={0}
                                        className="form-control"
                                        value={row.quantity ?? ''}
                                        onChange={(e) => {
                                            if (!onChangePendingRow) return;
                                            onChangePendingRow(idx, { quantity: Number(e.target.value || 0) });
                                        }}
                                        style={{ height: '38px' }}
                                    />
                                </div>

                                {/* Unidad */}
                                <div style={{ width: '100px', flexShrink: 0 }}>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={batch?.unit || selectedSalesOrderDetail?.quantity_unit || ''}
                                        readOnly
                                        disabled
                                        style={{ backgroundColor: '#f8f9fa', height: '38px' }}
                                    />
                                </div>

                                {/* Eliminar */}
                                {onRemoveRow && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-danger btn-sm"
                                        style={{ height: '38px', width: '40px', flexShrink: 0 }}
                                        onClick={() => onRemoveRow(idx)}
                                        title="Quitar lote"
                                    >
                                        <i className="mdi mdi-trash-can" />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Observaciones solo en edición (no en crear multi-lote) */}
            {is_edit && (
                <div className="mb-3">
                    <label className="form-label">Observaciones</label>
                    <textarea
                        className={`form-control ${fieldHasError('observations')}`}
                        name="observations"
                        value={detail.observations || ''}
                        onChange={handleChange}
                        placeholder="Ingrese observaciones"
                        onFocus={() => onFocusRemove('observations')}
                        rows={3}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('observations')}
                    </div>
                </div>
            )}
        </>
    );
};

export default DeliveryDetailForm;
